import React, { memo, useEffect, useState } from "react";
import weather from "../../utils/weather";
import axios from "axios";

const WeatherComponent = memo(({ link }) => {
  const [status, setStatus] = useState("");
  const [icon, setIcon] = useState(null);
  const [airTemperature, setAirTemperature] = useState("");

  const refreshInterval = 10 * 60 * 1000; // 10 minutes

  const Botanic_Latlong = { lat: 1.3139, lon: 103.8159 };
  const endpoints = ["air-temperature"];

  // calculate distance
  const haversine = (lat1, lon1, lat2, lon2) => {
    const toRad = (deg) => deg * (Math.PI / 180);
    const R = 6371; // Earth radius in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // interpolate result
  const interpolate = (target, stations) => {
    const weights = stations.map(({ location, value }) => {
      const dist = haversine(
        target.lat,
        target.lon,
        location.latitude,
        location.longitude,
      );
      const w = 1 / (dist + 1e-6);
      return { value, weight: w };
    });

    const numerator = weights.reduce(
      (sum, { value, weight }) => sum + value * weight,
      0,
    );
    const denominator = weights.reduce((sum, { weight }) => sum + weight, 0);
    return numerator / denominator;
  };
  // get status
  const getWeatherTerm = ({ temperature }) => {
    let heatStressLevel = "";

    if (temperature < 31) heatStressLevel = "Low Heat Stress";
    if (temperature >= 31 && temperature < 33)
      heatStressLevel = "Moderate Heat Stress";
    if (temperature >= 33) heatStressLevel = "High Heat Stress";

    setStatus(heatStressLevel);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const options = {
          method: "GET",
          url: "https://api-open.data.gov.sg/v2/real-time/api/weather?api=wbgt",
          headers: {
            "x-api-key":
              "v2:a91d3d0e22dd5b91345abe95fc35e84e34d36ae9c9f891bf16c3b8e69c3d473b:v0br3y7VoIP01ziwZkrC8FhdJXUS3Hfj", // this token only valid for dev purpose for 2 years until 13 Feb 2028, 21:10 SGT
          },
        };

        const resultWbgt = await axios.request(options);

        const readings = resultWbgt.data.data.records[0].item.readings;

        // Calculate median of valid wbgt values
        const validWbgt = readings
          .filter(
            (data) =>
              data && data.wbgt !== "NA" && !isNaN(parseFloat(data.wbgt)),
          )
          .map((data) => parseFloat(data.wbgt))
          .sort((a, b) => a - b);

        const medianWbgt =
          validWbgt.length > 0
            ? validWbgt.length % 2 === 0
              ? (validWbgt[validWbgt.length / 2 - 1] +
                  validWbgt[validWbgt.length / 2]) /
                2
              : validWbgt[Math.floor(validWbgt.length / 2)]
            : 0;

        // Map with NA replacement
        const categoriesWbgt = readings
          .map((data, i) => {
            return data
              ? {
                  location: {
                    latitude: parseFloat(data.location.latitude),
                    longitude: parseFloat(data.location.longitude),
                  },
                  value:
                    data.wbgt === "NA" || isNaN(parseFloat(data.wbgt))
                      ? medianWbgt
                      : parseFloat(data.wbgt),
                }
              : null;
          })
          .filter(Boolean);

        const wbgtData = interpolate(Botanic_Latlong, categoriesWbgt);

        const results = await Promise.all(
          endpoints.map((endpoint) =>
            // fetch(`https://api.data.gov.sg/v1/environment/${endpoint}`).then(
            fetch(
              `https://api-open.data.gov.sg/v2/real-time/api/${endpoint}`,
            ).then((res) => res.json()),
          ),
        );

        const categories = results?.map((data, i) => {
          const readings = data?.data?.readings;

          const stations = data?.data?.stations
            ?.map((station) => {
              const reading = readings[0].data.find(
                (r) => r.stationId === station.id,
              );
              return reading
                ? {
                    location: station.location,
                    value: reading.value,
                  }
                : null;
            })
            .filter(Boolean);

          return interpolate(Botanic_Latlong, stations);
        });

        const [temperature, humidity] = categories;

        setAirTemperature(Math.floor(temperature));

        getWeatherTerm({
          temperature,
        });

        const options24Forecast = {
          method: "GET",
          url: "https://api-open.data.gov.sg/v2/real-time/api/twenty-four-hr-forecast",
          headers: {
            "x-api-key":
              "v2:a91d3d0e22dd5b91345abe95fc35e84e34d36ae9c9f891bf16c3b8e69c3d473b:v0br3y7VoIP01ziwZkrC8FhdJXUS3Hfj", // this token only valid for dev purpose for 2 years until 13 Feb 2028, 21:10 SGT
          },
        };
      } catch (err) {
        console.error("Failed to fetch weather data:", err);
      }
    };

    fetchData();

    const interval = setInterval(fetchData, refreshInterval);

    return () => clearInterval(interval);
  }, []);

  return (
    <a
      className="flex flex-col font-avertaRegular text-[#434343] items-center justify-center"
      href={link}
      target="_blank"
    >
      <div className="flex flex-row items-center justify-center">
        {icon ? (
          icon
        ) : (
          <svg
            width="56"
            height="56"
            viewBox="0 0 56 56"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_3021_1110)">
              <path
                d="M49.7178 39.3838L48.7178 41.1162L44.1709 38.4912L45.1709 36.7588L49.7178 39.3838ZM28 12.25C36.6985 12.25 43.75 19.3015 43.75 28C43.75 32.3204 42.0093 36.2335 39.1924 39.0791C38.6969 38.6203 38.1482 38.219 37.5566 37.8838C40.1417 35.3838 41.75 31.8802 41.75 28C41.75 20.4061 35.5939 14.25 28 14.25C20.4061 14.25 14.25 20.4061 14.25 28C14.25 28.4564 14.273 28.9075 14.3164 29.3525C13.6596 29.6772 13.0343 30.0557 12.4453 30.4814C12.3174 29.6731 12.25 28.8444 12.25 28C12.25 19.3015 19.3015 12.25 28 12.25ZM8.75 29H3.5V27H8.75V29ZM52.5 29H47.25V27H52.5V29ZM11.8291 17.5088L10.8291 19.2412L6.28223 16.6162L7.28223 14.8838L11.8291 17.5088ZM49.7178 16.6162L45.1709 19.2412L44.1709 17.5088L48.7178 14.8838L49.7178 16.6162ZM19.2412 10.8291L17.5088 11.8291L14.8838 7.28223L16.6162 6.28223L19.2412 10.8291ZM41.1162 7.28223L38.4912 11.8291L36.7588 10.8291L39.3838 6.28223L41.1162 7.28223ZM29 8.75H27V3.5H29V8.75Z"
                fill="url(#paint0_linear_3021_1110)"
              />
              <path
                d="M20.4639 26.2539C26.0428 26.3953 30.7552 30.0184 32.5127 35.0312C32.7558 35.0109 33.0017 35 33.25 35L33.7002 35.0117C38.3233 35.2462 41.9999 39.0686 42 43.75L41.9883 44.2002C41.7539 48.8233 37.9314 52.4999 33.25 52.5L32.7998 52.4883C31.703 52.4327 30.6606 52.1731 29.707 51.75H24.502C23.1331 52.2351 21.6602 52.5 20.125 52.5L19.7861 52.4961C12.8067 52.3192 7.18087 46.6933 7.00391 39.7139L7 39.375C7.00001 32.1263 12.8763 26.25 20.125 26.25L20.4639 26.2539ZM20.125 28.25C13.9808 28.25 9.00001 33.2308 9 39.375C9.0001 45.5191 13.9809 50.5 20.125 50.5C20.1475 50.5 20.1699 50.4982 20.1924 50.498V49.75H24.1387C24.6809 49.5394 25.2028 49.288 25.7002 48.999H29.0078C29.3631 49.2867 29.7487 49.5383 30.1582 49.75H33.25V50.5C36.9778 50.4998 39.9999 47.4778 40 43.75C39.9999 40.0222 36.9778 37.0002 33.25 37C31.9315 37.0001 30.7015 37.3795 29.6621 38.0332C29.5646 37.3341 29.3926 36.6592 29.1533 36.0166C29.5908 35.7844 30.0507 35.5889 30.5283 35.4326C28.9368 31.2348 24.8807 28.25 20.125 28.25Z"
                fill="url(#paint1_linear_3021_1110)"
              />
            </g>
            <defs>
              <linearGradient
                id="paint0_linear_3021_1110"
                x1="28"
                y1="3.5"
                x2="28"
                y2="41.1162"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="#EFC977" />
                <stop offset="1" stop-color="#E07256" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_3021_1110"
                x1="24.5"
                y1="26.25"
                x2="24.5"
                y2="52.5"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="#B2D4F7" />
                <stop offset="1" stop-color="#D9E2F3" />
              </linearGradient>
              <clipPath id="clip0_3021_1110">
                <rect width="56" height="56" fill="white" />
              </clipPath>
            </defs>
          </svg>
        )}

        <p className="flex font-avertaSemiBold text-[28px] tracking-[.04em] text-white">
          {airTemperature}
          °C
        </p>
      </div>
      <div className="text-xs opacity-90 text-white">{status}</div>
    </a>
  );
});

export default WeatherComponent;
